import React, { memo, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { APP_CONFIG } from '@constants';
import { urlSearchParams } from '@utility/utills';
import { flowType } from '@utility/userInfo';
import { LABEL } from '../../../veriables';
import oldStyles from '../../OrderItemDetail/orderItem.module.scss';
import store from '@store';
import {
  pushModifierToItem,
  setNestedModifierArray,
  setTempModifier,
  removeItemFromCart,
} from '@reducers/orders';

const LinkedItemCard = memo(
  ({
    item,
    linkedItem,
    isSelected,
    linkedMenuItemId,
    onCardClick,
    menuItemsHashTable,
    isTableTop,
    auth,
  }) => {
    const price = linkedItem?.price || 0;
    const menuItemData = linkedItem?.menuItem;

    const serverAppImage = useMemo(() => {
      const image =
        menuItemsHashTable[linkedMenuItemId]?.serverAppAsImage &&
        menuItemsHashTable[linkedMenuItemId]?.intermediateImageUrl
          ? `url(${menuItemsHashTable[linkedMenuItemId]?.intermediateImageUrl})`
          : '';
      return image;
    }, [menuItemsHashTable, linkedMenuItemId]);

    const getBackgroundColor = useMemo(() => {
      return menuItemsHashTable[linkedMenuItemId]?.serverAppButtonColor || '';
    }, [menuItemsHashTable, linkedMenuItemId]);

    const handleError = useCallback(e => {
      e.target.style.visibility = 'hidden';
      e.target.style.height = '40px';
      e.target.className = 'col-12';
    }, []);

    const handleClick = useCallback(() => {
      onCardClick(linkedMenuItemId);
    }, [onCardClick, linkedMenuItemId]);

    return (
      <div
        className="col-4 pr-1 pl-0 pb-1"
        role="presentation"
        onClick={handleClick}
        style={{
          marginRight: '0px',
          height: '60px',
        }}
      >
        <div
          className={cn(
            !isSelected
              ? cn('w-100', oldStyles.colorSelected)
              : cn(oldStyles.selectedModifier, 'color-black'),
            'p-0 align-items-stretch flex-column h-100'
          )}
          style={{
            backgroundColor: getBackgroundColor || undefined,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundImage: serverAppImage,
          }}
        >
          {isTableTop && (
            <>
              {menuItemData?.mediumImageUrl ? (
                <div className="col-12 p-0">
                  <img
                    src={menuItemData?.mediumImageUrl}
                    className="img-fluid w-100 d-block mx-auto p-0"
                    style={{ objectFit: 'cover', height: '40px' }}
                    onError={handleError}
                    alt=""
                  />
                </div>
              ) : (
                <div
                  className="col-12 w-100 text-center w-100 p-0"
                  style={{ height: '40px', visibility: 'hidden' }}
                />
              )}
            </>
          )}
          <div
            className={cn(
              'h-100 w-100 m-0 p-0',
              !isTableTop ? '' : 'h-100',
              item?.name?.length <= 18 ? 'fs_16' : 'fs_14'
            )}
          >
            <div
              className={cn(
                isTableTop ? 'col-12' : 'col-12 h-100 d-flex align-items-start',
                'pl-1 row mx-0 p-0',
                isTableTop && item.name.length > 15 ? 'text-break fs-10' : ''
              )}
            >
              <span
                className={cn(
                  'col-12 px-0 h-70 fw-400',
                  item?.name?.length <= 18 ? 'fs_16' : 'fs_14'
                )}
              >
                {!menuItemsHashTable[linkedMenuItemId]?.serverAppAsImage &&
                  item.name}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const LinkedItems = ({
  menuCard,
  menusList,
  menuIndex,
  menuItemsHashTable,
  auth,
}) => {
  const { menuItemId, menuTypeId } = useParams();
  const navigate = useNavigate();
  const parentMenuItemId = urlSearchParams('parentMenuItemId');

  const isTableTop = useMemo(() => flowType() === 'tableTop', []);

  const parentItem = useMemo(() => {
    if (!parentMenuItemId || !menuCard[menuTypeId]) return null;
    return menuCard[menuTypeId][parentMenuItemId];
  }, [menuCard, menuTypeId, parentMenuItemId]);

  const links = useMemo(() => {
    return parentItem?.menuItem?.links || [];
  }, [parentItem]);

  const handleLinkedItemClick = useCallback(
    linkedMenuItemId => {
      if (String(linkedMenuItemId) === String(menuItemId)) return;
      const { orders } = store.getState();
      const { cart } = orders;

      store.dispatch(pushModifierToItem([]));
      store.dispatch(setNestedModifierArray([]));
      store.dispatch(setTempModifier([]));

      const existingLinkedCartItems = (cart || []).filter(
        c => c.isLinked === true
      );
      for (const c of existingLinkedCartItems) {
        store.dispatch(removeItemFromCart(c.internalId));
      }

      const menuId = menusList[menuIndex]?.id;
      navigate(
        `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${linkedMenuItemId}?menuId=${menuId}&parentMenuItemId=${parentMenuItemId}`
      );
    },
    [navigate, menuTypeId, menusList, menuIndex, parentMenuItemId, menuItemId]
  );

  if (!parentMenuItemId || links.length === 0) return null;

  return (
    <div className="d-flex m-0 w-100 pl-1 overflowX">
      {links.map(item => {
        const linkedItem = menuCard[menuTypeId]?.[item.linkedMenuItemId];
        if (!linkedItem) return null;

        const isSelected = String(item.linkedMenuItemId) === String(menuItemId);

        return (
          <LinkedItemCard
            key={item.linkedMenuItemId}
            item={item}
            linkedItem={linkedItem}
            isSelected={isSelected}
            linkedMenuItemId={item.linkedMenuItemId}
            onCardClick={handleLinkedItemClick}
            menuItemsHashTable={menuItemsHashTable}
            isTableTop={isTableTop}
            auth={auth}
          />
        );
      })}
    </div>
  );
};

const mapStateToProps = ({ orders, oauth }) => ({
  menuCard: orders.menuCardList,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
  menuItemsHashTable: orders.menuItemsHashTable,
  auth: oauth.authInfo,
});

export default connect(mapStateToProps)(memo(LinkedItems));
